import { ColumnsType } from 'antd/es/table';
import React, { FC, memo, useCallback, useMemo, useState } from 'react';
import { ServerTableActions, ServerTableRowActions } from 'shared/ui/ServerTable/types';
import { ServerTable } from 'shared/ui/ServerTable';
import { ReactComponent as EditUserTableIcon } from 'shared/assets/icons/EditUserTableIcon.svg';
import { ReactComponent as EditIcon } from 'shared/assets/icons/EditIcon.svg';
import { ReactComponent as PlusIcon } from 'shared/assets/icons/PlusIcon.svg';
import { ReactComponent as ContractIcon } from 'shared/assets/icons/ContractWhiteIcon.svg';
import { ReactComponent as ExportIcon } from 'shared/assets/icons/ExportIcon.svg';
import { ReactComponent as DeleteIcon } from 'shared/assets/icons/DeleteIcon.svg';
import { ReactComponent as FiltersIcon } from 'shared/assets/icons/FiltersIcon.svg';
import { ReactComponent as ContractsTableIcon } from 'shared/assets/icons/ContractsTableIcon.svg';
import { ReactComponent as CancelIcon } from 'shared/assets/icons/CancelIcon.svg';
import { ReactComponent as UploadIcon } from 'shared/assets/icons/UploadIcon.svg';
import { ReactComponent as DocumentIcon } from 'shared/assets/icons/DocumentIcon.svg';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import {
  Lead,
  useDeleteLeadMutation,
  useGetPaginatedLeadsQuery,
  useSendClarificationMutation,
  useSyncLeadAdsMutation,
} from 'entities/Lead';
import dayjs from 'dayjs';
import { Toggle } from 'shared/ui/Toggle';
import { getClientDateFormat } from 'shared/utils/helpers/getDateFormat';
import { LeadNoteDrawer, leadNoteDrawerActions } from 'features/LeadNoteDrawer';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { Paragraph } from 'shared/ui/Paragraph';
import { showNotification } from 'app/providers/NotificationsProvider';
import { sendOfferModalActions } from 'widgets/SendOfferModal';
import { AppLanguage } from 'app/config/i18Config/types';
import { stepperModalActions } from 'features/StepperModal';
import { ExportEntity, getLeadColumnKeys, useOpenExportModal } from 'features/ExportModal';
import { TableFilterType, useTableFilterContext } from 'features/TableFilter';
import { BadgeColor, RowBadge } from 'shared/ui/ServerTable/RowBadge';
import { EmailHistoryModal, useEmailHistoryModal } from 'widgets/EmailHistoryModal';
import { LeadsListFilter } from './LeadsListFilter';
import { transformLeadsListFilters } from '../utils/helpers/transformLeadsListFilters';
import { LeadFormModal, leadFormModalActions, LeadFormModalMode } from 'features/LeadFormModal';
import { DrawerMode, noteDrawerActions } from 'widgets/NoteDrawer';
import { AppRoutes } from 'app/config/routerConfig/types';
import { useNavigate } from 'react-router-dom';
import { RowSideBadge } from 'shared/ui/ServerTable/RowSideBadge';
import { useCheckPermission } from 'shared/utils/hooks/useCheckPermission';
import { getAccessToken, UserPermissions } from 'entities/User';
import { useGlobalConfirmModal } from 'app/globalConfirmModal';
import classNames from 'classnames';
import { LeftLeadModal } from 'features/LeftLeadModal';
import axios from 'axios';
import { API_URL } from 'app/config/envVariables';
import { getFeatureFlags } from 'app/appState';

interface LeadsTableProps {
  externalFilters: TableFilterType;
}

export const LeadsTable: FC<LeadsTableProps> = memo((props) => {
  const { externalFilters } = props;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { t } = useAppTranslation(['user', 'common']);

  const [deleteLead] = useDeleteLeadMutation();
  const [sendClarification] = useSendClarificationMutation();
  const [syncLeadAds, { isLoading: isSyncLoading }] = useSyncLeadAdsMutation();
  const [isExporting, setIsExporting] = useState(false);

  const isForbiddenDelete = useCheckPermission(UserPermissions.DELETE_UNITS);

  const { openExportModal, isExportEnabled } = useOpenExportModal();

  const { openModal, closeModal } = useGlobalConfirmModal();

  const { setFiltersOpened, appliedFilters, tags, clearAllFilters } = useTableFilterContext();

  const featureFlags = useAppSelector(getFeatureFlags);
  const isSyncAdsEnabled = Boolean(featureFlags?.adsSync);

  const queryParams = useMemo(() => {
    return transformLeadsListFilters(appliedFilters);
  }, [appliedFilters]);

  const { openEmailHistoryModal } = useEmailHistoryModal();

  const handleOpenLeadNoteDrawer = useCallback(
    (lead: Lead): void => {
      dispatch(leadNoteDrawerActions.setOpenLeadNoteDrawer(lead));
    },
    [dispatch],
  );

  const handleSendClarification = useCallback(
    async (email?: Nullable<string>): Promise<void> => {
      try {
        if (email) {
          await sendClarification(email).unwrap();

          closeModal();

          showNotification('info', t('Success', { ns: 'common' }), t('Clarification mail has been sent successfully'));
        }
      } catch (error) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when sending clarification mail'));
        console.log('error', error);
      }
    },
    [t, sendClarification, closeModal],
  );

  const openSendOfferModal = useCallback((): void => {
    dispatch(sendOfferModalActions.setOpened(true));
  }, [dispatch]);

  const handleSelectOfferModalLead = useCallback(
    (lead: Lead): void => {
      if (lead?.email) {
        dispatch(sendOfferModalActions.setSelectedEmail(lead.email));
        dispatch(sendOfferModalActions.setSelectedLanguage(lead.language as AppLanguage));
        dispatch(stepperModalActions.changeActiveStepIndex(1));
      }

      if (lead?.userId) {
        dispatch(sendOfferModalActions.setSelectedUserId(lead.userId));
      }

      openSendOfferModal();
    },
    [dispatch, openSendOfferModal],
  );

  const createLead = useCallback((): void => {
    dispatch(leadFormModalActions.setOpenLeadFormModal({ mode: LeadFormModalMode.LEAD_CREATE }));
  }, [dispatch]);

  const editLead = useCallback(
    (lead: Lead): void => {
      dispatch(leadFormModalActions.setOpenLeadFormModal({ mode: LeadFormModalMode.LEAD_EDIT, lead }));
    },
    [dispatch],
  );

  const createNote = useCallback(
    (lead: Lead): void => {
      dispatch(noteDrawerActions.setOpenNoteDrawer({ mode: DrawerMode.NOTE_CREATE, lead }));
    },
    [dispatch],
  );

  const openClarificationConfirmModal = useCallback(
    (email?: Nullable<string>): void => {
      openModal({
        title: t('Send clarification mail'),
        description: t('Are you sure you want to send clarification mail?'),
        onCancel: closeModal,
        onOk: async () => {
          await handleSendClarification(email);
        },
        isConfirmDisabled: !email,
      });
    },
    [closeModal, handleSendClarification, openModal, t],
  );

  const handleDeleteLead = useCallback(
    async (selectedLead: Lead): Promise<void> => {
      try {
        await deleteLead(selectedLead.leadId).unwrap();
        showNotification('info', t('Success', { ns: 'common' }), t('Lead deleted successfully'));
      } catch (error) {
        showNotification('error', t('Error', { ns: 'common' }), t('Error when deleting lead'));
        console.log('error', error);
      } finally {
        closeModal();
      }
    },
    [deleteLead, t, closeModal],
  );

  const openLeadDeletionConfirmModal = useCallback(
    (selectedLead: Lead): void => {
      openModal({
        title: t('Delete lead'),
        description: t('Are you sure you want to delete this lead?'),
        onCancel: closeModal,
        onOk: async () => {
          await handleDeleteLead(selectedLead);
        },
        isConfirmDisabled: isForbiddenDelete,
      });
    },
    [closeModal, handleDeleteLead, isForbiddenDelete, openModal, t],
  );

  const leadRowActions: Array<ServerTableRowActions<Lead>> = useMemo(
    () => [
      {
        name: 'editLead',
        icon: <EditUserTableIcon />,
        theme: 'clear',
        description: t('Edit lead'),
        onClick: (selectedLead) => {
          editLead(selectedLead);
        },
      },
      {
        name: 'editLeadNote',
        icon: <EditIcon />,
        theme: 'clear',
        description: t('Edit note'),
        onClick: (selectedLead) => {
          handleOpenLeadNoteDrawer(selectedLead);
        },
      },
      // {
      //   name: 'leadProfile',
      //   icon: <ProfileIcon />,
      //   theme: 'clear',
      //   onClick: (selectedUser) => {
      //     console.log(selectedUser);
      //   },
      // },
      {
        name: 'deleteLead',
        icon: <DeleteIcon />,
        theme: 'clear',
        description: t('Delete lead'),
        onClick: (selectedLead: Lead) => {
          openLeadDeletionConfirmModal(selectedLead);
        },
      },
      {
        name: 'sendOffer',
        theme: 'clear',
        label: t('Send offer'),
        description: t('Send offer'),
        icon: <ContractIcon className="[&>path]:fill-primary" />,
        onClick: (selectedUser) => {
          handleSelectOfferModalLead(selectedUser);
        },
      },
      {
        name: 'sendСlarification',
        theme: 'clear',
        label: t('Send clarification'),
        description: t('Send clarification'),
        icon: <CancelIcon />,
        onClick: (selectedLead) => {
          openClarificationConfirmModal(selectedLead.email);
        },
      },
      {
        name: 'createNote',
        theme: 'clear',
        label: t('Create Note'),
        description: t('Create Note'),
        icon: <PlusIcon className="[&>path]:stroke-primary" />,
        onClick: createNote,
      },
      {
        name: 'emailHistory',
        theme: 'clear',
        label: t('Offer history'),
        description: t('Offer history'),
        icon: <DocumentIcon className="[&>path]:stroke-primary" />,
        onClick: (selectedLead) => {
          selectedLead.email && openEmailHistoryModal(selectedLead.email);
        },
      },
    ],
    [
      createNote,
      editLead,
      handleOpenLeadNoteDrawer,
      handleSelectOfferModalLead,
      openClarificationConfirmModal,
      openLeadDeletionConfirmModal,
      openEmailHistoryModal,
      t,
    ],
  );

  const goToUserContracts = useCallback(
    (lead: Lead): void => {
      navigate(`${AppRoutes.USERS}/${lead.userId}/contracts`);
    },
    [navigate],
  );

  const handleSyncLeadAds = useCallback(async (): Promise<void> => {
    try {
      await syncLeadAds().unwrap();

      showNotification('info', t('Success', { ns: 'common' }), t('Ads synced successfully'));
    } catch (error) {
      showNotification('error', t('Error', { ns: 'common' }), t('Error when syncing ads'));
      console.log('error', error);
    }
  }, [syncLeadAds, t]);

  const handleExportAds = useCallback(async () => {
    try {
      setIsExporting(true);
      const token = getAccessToken();

      const response = await axios.post(
        `${API_URL}/leads/ads/export`,
        { filters: queryParams },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          responseType: 'blob',
        },
      );

      const blob = new Blob([response.data], {
        type: response.data.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = 'leads-ads-export.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      showNotification('info', t('Success'), t('Downloaded!'));
    } catch (error) {
      console.error('Error downloading the file', error);
      showNotification('error', t('Error'), t('Error when downloading data'));
    } finally {
      setIsExporting(false);
    }
  }, [queryParams, t]);

  const leadColumns = useMemo<ColumnsType<Lead>>(
    () => [
      {
        title: t('Name'),
        key: 'name',
        render: (_, record) => {
          const isNewLead = dayjs().diff(dayjs(record.createdAt), 'day') < 7 && !record?.contractId;

          const isExpiredReservation = dayjs().diff(dayjs(record.reservationDate), 'day') > 3;

          let badgeColor: BadgeColor = 'success';

          if (record.isContractPaid && !record.isLeft) badgeColor = 'accent';
          if (record.contractId && !record.isLeft && !record.isContractPaid && !record.contractEndDate) badgeColor = 'warn';
          if (record.reservationId && !record.contractId && !record.isLeft && !record.isContractPaid && !record.contractEndDate)
            badgeColor = 'primaryLight';
          if (record.reservationId && !record.contractId && isExpiredReservation && !record.isLeft) badgeColor = 'error';
          if (record.contractId && record.contractEndDate && !record.isLeft) badgeColor = 'error';
          if (record.userId && !record.reservationId && !record.contractId && !record.isLeft) badgeColor = 'success';
          if (record.isLeft) badgeColor = 'primary';

          let badgeTitle = t('Active requests');

          if (record.isContractPaid) badgeTitle = t('Paid');
          if (record.contractId && !record.isContractPaid && !record.isLeft) badgeTitle = t('Not paid');
          if (record.reservationId && !record.contractId && !record.isLeft) badgeTitle = t('Reservation');
          if (record.reservationId && !record.contractId && isExpiredReservation && !record.isLeft) badgeTitle = t('Expired reservation');
          if (record.contractId && record.contractEndDate && !record.isLeft) badgeTitle = t('End');
          if (record.userId && !record.reservationId && !record.contractId && !record.isLeft) badgeTitle = t('Without bookings');
          if (record.isLeft) badgeTitle = t('Left');

          return (
            <div>
              {record.firstName ? (
                <span className="text-primary font-semibold">
                  {record.firstName} {record.lastName || ''}
                </span>
              ) : (
                'N/A'
              )}
              {isNewLead && <sup className="text-sm text-success p-1">{t('New')}</sup>}
              <RowBadge color={badgeColor} title={badgeTitle} />
              {record.isAutoMailingEnabled && (
                <RowSideBadge color="success" toolTipColor="white" toolTipFontColor="primary" title={t('Automailing enabled')} />
              )}
            </div>
          );
        },
      },
      {
        title: t('Email/Phone'),
        key: 'email',
        render: (_, record) => (
          <div>
            {record.email && (
              <div className="flex gap-2 text-primary font-normal text-sm">
                {record.email}
                {record.userId && (
                  <ContractsTableIcon
                    className="cursor-pointer"
                    onClick={() => {
                      goToUserContracts(record);
                    }}
                  />
                )}
              </div>
            )}
            {record.phone && <div className="text-primary font-normal text-sm">{record.phone}</div>}
          </div>
        ),
      },
      {
        title: t('Warehouse'),
        key: 'warehouseId',
        render: (_, record) => (
          <>
            {record.warehouse && (
              <div className="text-primary">
                <div className="font-normal text-sm">{record.warehouse.name}</div>
                <div className="font-normal text-sm">{record.warehouse.address}</div>
              </div>
            )}
          </>
        ),
      },
      {
        title: t('Date'),
        key: 'createdAt',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => <div className="text-primary font-normal text-sm">{getClientDateFormat(record.createdAt)}</div>,
      },
      {
        title: t('Box size'),
        key: 'square',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => (
          <>
            {record.square && (
              <div className="text-sm">
                {record.square}m<sup>2</sup>
              </div>
            )}
          </>
        ),
      },
      {
        title: t('Status'),
        key: 'isLeft',
        sorter: true,
        sortDirections: ['ascend', 'descend', 'ascend'],
        render: (_, record) => (
          <>
            <LeftLeadModal
              lead={record}
              action={
                <div>
                  <Toggle checked={!record.isLeft} />
                  <div className="text-xs text-primaryLight mt-2">{t(!record.isLeft ? 'Relevant' : 'Irrelevant')}</div>
                </div>
              }
            />
            {record.isLeft && record?.leaveReason && <div className="text-sm">{t(record.leaveReason)}</div>}
          </>
        ),
      },
      {
        title: t('Note'),
        key: 'note',
        render: (_, record) => {
          const paragraphs = record.note?.split('\n');

          const paragraphElements = paragraphs?.map((paragraph: string, index: number) => (
            <p key={index} className="font-normal !mb-0">
              {paragraph}
            </p>
          ));

          return <Paragraph rows={3}>{paragraphElements}</Paragraph>;
        },
      },
    ],
    [goToUserContracts, t],
  );

  const leadTableActions: Array<ServerTableActions<Lead>> = useMemo(() => {
    const exportAction: Array<ServerTableActions<Lead>> = isExportEnabled
      ? [
          {
            name: t('Export', { ns: 'common' }),
            theme: 'secondary',
            icon: <ExportIcon />,
            iconPosition: 'prev',
            onClick: () => {
              openExportModal({
                filters: transformLeadsListFilters(appliedFilters),
                columns: getLeadColumnKeys(t),
                entity: ExportEntity.LEAD,
              });
            },
          },
        ]
      : [];

    const syncAdsAction: Array<ServerTableActions<Lead>> = isSyncAdsEnabled
      ? [
          {
            name: t('Sync Ads'),
            theme: 'secondary',
            icon: <UploadIcon className="w-6 h-6" />,
            iconPosition: 'prev',
            loading: isSyncLoading,
            onClick: handleSyncLeadAds,
          },
        ]
      : [];

    return [
      {
        name: t('Send offer'),
        theme: 'primary',
        icon: <ContractIcon />,
        iconPosition: 'prev',
        onClick: openSendOfferModal,
      },
      {
        name: t('Create Lead'),
        theme: 'secondary',
        icon: <PlusIcon className="[&>path]:stroke-primary" />,
        iconPosition: 'prev',
        onClick: createLead,
      },
      ...exportAction,
      ...syncAdsAction,
      {
        name: t('Filters', { ns: 'common' }),
        icon: <FiltersIcon />,
        theme: 'secondary',
        iconPosition: 'prev',
        onClick: () => {
          setFiltersOpened(true);
        },
      },
    ];
  }, [
    isExportEnabled,
    t,
    isSyncAdsEnabled,
    isSyncLoading,
    openSendOfferModal,
    createLead,
    openExportModal,
    appliedFilters,
    handleSyncLeadAds,
    setFiltersOpened,
  ]);

  const highlightRow = (record: Lead): string => {
    const hasActiveNotes = record.assignedNotes?.length;
    const hasActiveWaitingListEntries = record.assignedWaitingListEntries?.length;

    return classNames({
      'bg-successLight': hasActiveNotes,
      'bg-warnLight': hasActiveWaitingListEntries && !hasActiveNotes,
    });
  };

  return (
    <div className="space-y-4">
      <ServerTable
        columns={leadColumns}
        rowActions={leadRowActions}
        tableActions={leadTableActions}
        tableActionsPosition="prev"
        fetchData={useGetPaginatedLeadsQuery}
        defaultFilters={transformLeadsListFilters(appliedFilters)}
        rowKey="leadId"
        search
        searchPlaceholder={t('Enter lead`s name, phone or email')}
        tags={tags}
        onAllFiltersClear={clearAllFilters}
        rowActionsLimit={3}
        defaultSearch={externalFilters.search?.value}
        rowClassName={highlightRow}
        tableActionsLimit={5}
      />
      <LeadFormModal />
      <LeadNoteDrawer />
      <LeadsListFilter />
      <EmailHistoryModal />
    </div>
  );
});
